import React from 'react';

/**
 * InboxWorkspaceTabs Component
 * Implements the 6 primary operational workspace tabs:
 * [All] [Activity] [Action Required] [@Mentioned] [Bookmarks] [Archive]
 */
export default function InboxWorkspaceTabs({ activeTab, onTabChange, counts = {} }) {
    const tabs = [
        {
            id: 'all',
            label: 'All',
            icon: 'apps',
            count: counts.all ?? 0,
            iconColor: 'text-blue-700 dark:text-[#00AAF1]',
            activeClass: 'border-blue-600 dark:border-[#00AAF1] ring-2 ring-blue-500/20 text-blue-900 dark:text-[#00AAF1] font-bold bg-white dark:bg-[#1b253b]',
            badgeClass: 'bg-blue-100 dark:bg-blue-950/60 text-blue-800 dark:text-blue-300 border-blue-200 dark:border-blue-800',
            hoverBorder: 'hover:border-blue-300 dark:hover:border-blue-700',
        },
        {
            id: 'activity',
            label: 'Activity',
            icon: 'trending_up',
            count: counts.activity ?? 0,
            iconColor: 'text-emerald-600 dark:text-emerald-400',
            activeClass: 'border-emerald-600 dark:border-emerald-500 ring-2 ring-emerald-500/20 text-emerald-900 dark:text-emerald-300 font-bold bg-white dark:bg-[#142e23]',
            badgeClass: 'bg-emerald-50 dark:bg-emerald-950/60 text-emerald-800 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800',
            hoverBorder: 'hover:border-emerald-300 dark:hover:border-emerald-700',
        },
        {
            id: 'action',
            label: 'Action Required',
            icon: 'pending_actions',
            count: counts.action ?? 0,
            iconColor: 'text-red-600 dark:text-red-400',
            activeClass: 'border-red-600 dark:border-red-500 ring-2 ring-red-500/20 text-red-900 dark:text-red-300 font-bold bg-white dark:bg-[#34181c]',
            badgeClass: 'bg-red-100 dark:bg-red-950/60 text-red-600 dark:text-red-300 border-red-200 dark:border-red-800',
            hoverBorder: 'hover:border-red-300 dark:hover:border-red-700',
        },
        {
            id: 'mentions',
            label: '@Mentioned',
            icon: 'alternate_email',
            count: counts.mentions ?? 0,
            iconColor: 'text-purple-600 dark:text-purple-400',
            activeClass: 'border-purple-600 dark:border-purple-500 ring-2 ring-purple-500/20 text-purple-900 dark:text-purple-300 font-bold bg-white dark:bg-[#291738]',
            badgeClass: 'bg-purple-100 dark:bg-purple-950/60 text-purple-700 dark:text-purple-300 border-purple-200 dark:border-purple-800',
            hoverBorder: 'hover:border-purple-300 dark:hover:border-purple-700',
        },
        {
            id: 'bookmarks',
            label: 'Bookmarks',
            icon: 'bookmark',
            count: counts.bookmarks,
            iconColor: 'text-slate-500 dark:text-slate-400',
            activeClass: 'border-slate-600 dark:border-slate-500 ring-2 ring-slate-400/20 text-slate-900 dark:text-slate-100 font-bold bg-white dark:bg-[#252525]',
            badgeClass: 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700',
            hoverBorder: 'hover:border-slate-300 dark:hover:border-slate-600',
        },
        {
            id: 'archive',
            label: 'Archive',
            icon: 'inventory_2',
            count: counts.archive,
            iconColor: 'text-slate-500 dark:text-slate-400',
            activeClass: 'border-slate-600 dark:border-slate-500 ring-2 ring-slate-400/20 text-slate-900 dark:text-slate-100 font-bold bg-white dark:bg-[#252525]',
            badgeClass: 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700',
            hoverBorder: 'hover:border-slate-300 dark:hover:border-slate-600',
        },
    ];

    return (
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-slate-200/60 dark:border-[#303030] bg-slate-50/50 dark:bg-[#141414]">
            <div className="flex flex-wrap items-center gap-2" id="feed-filter-nav">
                {tabs.map((tab) => {
                    const isActive = activeTab === tab.id;
                    return (
                        <button
                            key={tab.id}
                            id={`tab-${tab.id}`}
                            data-testid={`tab-${tab.id}`}
                            onClick={() => onTabChange(tab.id)}
                            className={`
                                group px-3.5 py-1.5 rounded-xl flex items-center gap-2 text-left transition-all cursor-pointer transform hover:-translate-y-0.5 border-2
                                ${isActive
                                    ? `${tab.activeClass} shadow-sm`
                                    : `bg-white/90 dark:bg-[#1f1f1f] hover:bg-white dark:hover:bg-[#262626] border-transparent ${tab.hoverBorder} text-slate-700 dark:text-slate-300 shadow-2xs`
                                }
                            `}
                        >
                            <span className={`material-symbols-outlined text-base ${tab.iconColor}`}>
                                {tab.icon}
                            </span>
                            <span className={`text-sm font-headline ${isActive ? 'font-bold' : 'font-semibold'}`}>
                                {tab.label}
                            </span>
                            {typeof tab.count === 'number' && (
                                <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${tab.badgeClass}`}>
                                    {tab.count}
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}
